import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import ProfileSettings from '@Services/ProfileSettings';
import CommercialSubstancesPreferencesEditor from '@Components/CommercialSubstancesPreferences/CommercialSubstancesPreferencesEditor';
import {
  COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION,
  INITIAL_SELECTIONS,
  SUPPLIERS_TAB_ID,
  TAB_CONFIGS,
  getPreferenceOptionsByTab,
  getPreferencePath,
  getPreferenceSelections,
  getTabConfig,
} from '@Components/CommercialSubstancesPreferences/CommercialSubstancesPreferencesConstants';
import { ButtonWithIcon } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { Pill } from '@els/els-react--pill';
import BlockHeading from '../../BlockHeading';

import './PreferencesCommercialSubstancesSection.scss';

const EMPTY_SEARCH_VALUES = TAB_CONFIGS.reduce(
  (accumulator, tab) => ({
    ...accumulator,
    [tab.id]: '',
  }),
  {}
);

function getOptionsById(options = []) {
  return options.reduce((accumulator, option) => {
    accumulator[option.id] = option;
    return accumulator;
  }, {});
}

function getSelectedLabels(tabId, selectedValues, optionsByTab) {
  const optionsById = getOptionsById(optionsByTab[tabId]);
  return selectedValues.map((selectedValue) => optionsById[selectedValue]?.label || selectedValue);
}

function PreferenceRow({ tabId, selectedValues, optionsByTab, isOpen, onEdit, editButtonRef = undefined }) {
  const tabConfig = getTabConfig(tabId);
  const selectedLabels = getSelectedLabels(tabId, selectedValues, optionsByTab);

  return (
    <div className="profile-commercial-substances-preferences__row" data-preference-tab={tabId}>
      <h3 className="profile-commercial-substances-preferences__row-title">{tabConfig.label}</h3>
      {selectedLabels.length ? (
        <div className="profile-commercial-substances-preferences__row-pills" aria-label={`${tabConfig.label} selected preferences`}>
          {selectedValues.map((selectedValue, index) => (
            <Pill key={selectedValue} pillColor="n1" textColor="text">
              {selectedLabels[index]}
            </Pill>
          ))}
        </div>
      ) : null}
      <ButtonWithIcon
        ref={editButtonRef}
        type="secondary"
        size="small"
        sprite={isOpen ? 'Close' : 'Edit'}
        linkIconSize="s"
        iconOnly
        className="profile-commercial-substances-preferences__row-edit"
        data-e2e={`profile-commercial-substances-preferences-${tabId}-edit`}
        aria-expanded={isOpen}
        onClick={() => onEdit(tabId)}>
        {isOpen ? `Close ${tabConfig.label}` : `Edit ${tabConfig.label}`}
      </ButtonWithIcon>
    </div>
  );
}

PreferenceRow.propTypes = {
  tabId: PropTypes.string.isRequired,
  selectedValues: PropTypes.array.isRequired,
  optionsByTab: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  editButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};

function PreferencesCommercialSubstancesSection({ editUser, openSupplierPreferences, onChange, onSave, onClose, onReset }) {
  const optionsByTab = useMemo(() => getPreferenceOptionsByTab(ProfileSettings.getCombinedInfo()), []);
  const supplierOptions = useMemo(() => optionsByTab[SUPPLIERS_TAB_ID] || [], [optionsByTab]);
  const supplierOptionsByName = useMemo(
    () =>
      supplierOptions.reduce((accumulator, option) => {
        accumulator[option.name] = option;
        return accumulator;
      }, {}),
    [supplierOptions]
  );
  const currentSelections = useMemo(() => getPreferenceSelections(editUser, optionsByTab), [editUser, optionsByTab]);
  const [activeTab, setActiveTab] = useState(openSupplierPreferences ? SUPPLIERS_TAB_ID : null);
  const [draftSelections, setDraftSelections] = useState({
    ...currentSelections,
  });
  const [searchValues, setSearchValues] = useState(EMPTY_SEARCH_VALUES);
  const editButtonRefs = useRef({});
  const previousActiveTabRef = useRef(activeTab);
  const restoreFocusOnCloseRef = useRef(true);

  // Restore focus before the removed editor controls can drop focus to the document.
  useLayoutEffect(() => {
    const previousActiveTab = previousActiveTabRef.current;
    if (previousActiveTab && !activeTab) {
      if (restoreFocusOnCloseRef.current) {
        editButtonRefs.current[previousActiveTab]?.focus();
      }
      restoreFocusOnCloseRef.current = true;
    }
    previousActiveTabRef.current = activeTab;
  }, [activeTab]);

  const openEditor = (tabId) => {
    if (activeTab === tabId) {
      setActiveTab(null);
      return;
    }

    setDraftSelections({
      ...TAB_CONFIGS.reduce(
        (accumulator, tab) => ({
          ...accumulator,
          [tab.id]: [...(currentSelections[tab.id] || [])],
        }),
        {}
      ),
    });
    setSearchValues(EMPTY_SEARCH_VALUES);
    setActiveTab(tabId);
  };

  const updateDraftSelection = (tabId, value, checked) => {
    setDraftSelections((currentDraftSelections) => {
      const existingSelections = currentDraftSelections[tabId] || [];
      const nextSelections = checked ? [...existingSelections, value] : existingSelections.filter((item) => item !== value);

      return {
        ...currentDraftSelections,
        [tabId]: nextSelections,
      };
    });

    onChange([...getPreferencePath(tabId), value], checked);
  };

  const clearDraftSelection = (tabId) => {
    setDraftSelections((currentDraftSelections) => ({
      ...currentDraftSelections,
      [tabId]: [],
    }));

    (optionsByTab[tabId] || []).forEach((option) => {
      onChange([...getPreferencePath(tabId), option.id], false);
    });
  };

  const closeEditor = () => {
    restoreFocusOnCloseRef.current = true;
    setDraftSelections(INITIAL_SELECTIONS);
    onClose(COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION, () => setActiveTab(null));
  };

  const saveEditor = () => {
    restoreFocusOnCloseRef.current = true;
    onSave(COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION, () => setActiveTab(null), activeTab);
  };

  const resetToDefault = () => {
    restoreFocusOnCloseRef.current = true;
    setDraftSelections(INITIAL_SELECTIONS);
    onReset(COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION, [COMMERCIAL_SUBSTANCES_PREFERENCES_SECTION], () => setActiveTab(null));
  };

  const handleEditorSubmit = (event) => {
    event.preventDefault();
  };

  const tabIds = TAB_CONFIGS.map((tab) => tab.id);
  const renderEditorCard = (tabId) => {
    const tabConfig = getTabConfig(tabId);

    return (
      <Card
        key={tabId}
        className="profile-commercial-substances-preferences__editor-card"
        borderWeight="light"
        radius="m"
        size="small"
        heading={
          <div className="profile-commercial-substances-preferences__editor-card-heading">
            {/* the editor card replaces the PreferenceRow, so this is its only heading while open */}
            <h3 className="profile-commercial-substances-preferences__row-title u-els-margin-none">{tabConfig.label}</h3>
            <ButtonWithIcon
              type="secondary"
              sprite="Close"
              iconOnly
              data-e2e={`profile-commercial-substances-preferences-${tabId}-close`}
              onClick={closeEditor}>
              Close {tabConfig.label}
            </ButtonWithIcon>
          </div>
        }>
        <form className="profile-commercial-substances-preferences__editor-form" noValidate onSubmit={handleEditorSubmit}>
          <CommercialSubstancesPreferencesEditor
            idPrefix={`profile-commercial-substances-preferences-${tabId}`}
            panelId={`profile-commercial-substances-preferences-${tabId}-panel`}
            activeTab={tabId}
            selections={draftSelections}
            searchValues={searchValues}
            supplierOptions={supplierOptions}
            supplierOptionsByName={supplierOptionsByName}
            optionsByTab={optionsByTab}
            onSearchChange={(nextTabId, value) => {
              setSearchValues((currentSearchValues) => ({
                ...currentSearchValues,
                [nextTabId]: value,
              }));
            }}
            onToggleSelection={updateDraftSelection}
            onClearSelection={clearDraftSelection}
            onCancel={closeEditor}
            onSave={saveEditor}
            saveLabel="Save"
            cancelLabel="Cancel"
            showTabs={false}
            showContentHeader={false}
            className="commercial-substances-preferences-editor--profile-card"
          />
        </form>
      </Card>
    );
  };

  return (
    <section className="profile-commercial-substances-preferences u-els-padding-top-1x1o2" aria-label="Commercial substances preferences">
      <BlockHeading title="Commercial substances preferences" withResetButton onResetClick={resetToDefault} />

      <div className="profile-commercial-substances-preferences__rows">
        {tabIds.map((tabId) => (
          <React.Fragment key={tabId}>
            {activeTab === tabId ? (
              renderEditorCard(tabId)
            ) : (
              <PreferenceRow
                tabId={tabId}
                selectedValues={currentSelections[tabId] || []}
                optionsByTab={optionsByTab}
                isOpen={activeTab === tabId}
                onEdit={openEditor}
                editButtonRef={(element) => {
                  editButtonRefs.current[tabId] = element;
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

PreferencesCommercialSubstancesSection.propTypes = {
  editUser: PropTypes.object,
  openSupplierPreferences: PropTypes.bool,
  onChange: PropTypes.func,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onReset: PropTypes.func,
};

PreferencesCommercialSubstancesSection.defaultProps = {
  onChange: noop,
  onSave: noop,
  onClose: noop,
  onReset: noop,
};

export default PreferencesCommercialSubstancesSection;
